import type { MapPoint as MapPointData } from '@elara/client-core/ui-contracts/map-point';

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { Env } from '#env.js';
import { useUserProfileState } from '#states/hooks.js';

/**
 * The web map point: the platform-neutral {@link MapPointData} (shared with the
 * data models via `@elara/client-core`) plus the web-only `onClick` that takes
 * `google.maps` handles. Re-exported as `MapPoint` so existing `#ui` consumers
 * are unchanged (ELARA-700).
 */
export interface MapPoint extends MapPointData {
  onClick?: (map: google.maps.Map, position: google.maps.LatLng) => void;
}

export interface GoogleMapProps {
  points: MapPoint[];
  center?: { latitude: number; longitude: number };
  zoom?: number;
  showLabels?: boolean;
  /** When true, draws a line between points in order (e.g. for flights or routes). Requires at least 2 valid points. */
  showPolyline?: boolean;
  /** Optional styling for the polyline. Geodesic is true by default for travel (great-circle) lines. */
  polylineOptions?: { strokeColor?: string; strokeWeight?: number; strokeOpacity?: number; geodesic?: boolean };
  /** When false, hides controls and disables pan/zoom. Default true. */
  interactive?: boolean;
  className?: string;
  onMapReady?: (map: google.maps.Map) => void;
  onError?: (error: string | null) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export interface GoogleMapRef {
  fitBounds: () => void;
}

/**
 * GoogleMap is a core component that provides Google Maps functionality.
 * It handles map initialization, marker creation, geocoding, and map interactions.
 * This component does not include any UI wrapper - it's just the map itself.
 *
 * @param points - Array of points to display on the map
 * @param center - Optional center point for the map
 * @param zoom - Initial zoom level (default: 12)
 * @param showLabels - When true, shows labels for each point (default true)
 * @param showPolyline - When true, draws a line between points in order (e.g. for flights)
 * @param polylineOptions - Optional styling for the polyline (geodesic defaults to true for travel)
 * @param interactive - When false, hides controls and disables pan/zoom (default true)
 * @param className - Optional CSS class for the container
 * @param onMapReady - Callback when map is initialized
 * @param onError - Callback when an error occurs
 * @param onLoadingChange - Callback when loading state changes
 */
export const GoogleMap = React.forwardRef<GoogleMapRef, GoogleMapProps>(
  (
    {
      points,
      center,
      zoom = 12,
      showLabels = true,
      showPolyline = false,
      polylineOptions,
      interactive = true,
      className,
      onMapReady,
      onError,
      onLoadingChange,
    },
    ref,
  ) => {
    const userProfile = useUserProfileState().profile;

    // Refs for Google Maps
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const polylineRef = useRef<google.maps.Polyline | null>(null);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

    // Memoize valid points calculation
    const validPoints = useMemo(() => {
      if (!points || points.length === 0) {
        return [];
      }

      const filtered = points.filter((point) => {
        const hasCoordinates =
          typeof point.latitude === 'number' &&
          typeof point.longitude === 'number' &&
          point.latitude >= -90 &&
          point.latitude <= 90 &&
          point.longitude >= -180 &&
          point.longitude <= 180;

        const hasAddress = typeof point.address === 'string' && point.address.trim().length > 0;

        const isValid = hasCoordinates || hasAddress;

        if (!isValid) {
          console.warn(
            `[GoogleMap] Skipping invalid point "${point.label || point.id}": must have either valid coordinates (lat: ${point.latitude}, lng: ${point.longitude}) or address (${point.address})`,
          );
        }
        return isValid;
      });

      // Log summary if some points were filtered out
      if (filtered.length < points.length) {
        console.info(
          `[GoogleMap] Filtered ${points.length - filtered.length} invalid points, showing ${filtered.length} valid points`,
        );
      }

      return filtered;
    }, [points]);

    // Memoize map center calculation - only used if center prop is provided
    const mapCenter = useMemo(() => {
      if (center) {
        return center;
      }
      // Default center - will be overridden by fitBounds if there are markers
      return { latitude: 0, longitude: 0 };
    }, [center]);

    // Get theme preference from user profile (independent of google being available)
    const themePreference = useMemo(() => {
      return userProfile?.theme || 'system';
    }, [userProfile?.theme]);

    // Track system theme changes to trigger map re-initialization when preference is 'system'
    const [systemThemeKey, setSystemThemeKey] = useState(0);

    // Memoize map configuration to prevent unnecessary re-initializations
    const mapConfig = useMemo(
      () => ({
        center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: interactive,
        disableDefaultUI: !interactive,
        gestureHandling: interactive ? 'cooperative' : 'none',
        draggable: interactive,
        disableDoubleClickZoom: !interactive,
        ...(Env.VITE_GOOGLE_MAPS_MAP_ID && { mapId: Env.VITE_GOOGLE_MAPS_MAP_ID }),
      }),
      [mapCenter.latitude, mapCenter.longitude, zoom, interactive],
    );

    // Create a stable reference for validPoints to prevent unnecessary re-initializations
    const validPointsRef = useRef(validPoints);
    validPointsRef.current = validPoints;

    // Store callbacks in refs to avoid re-initializing map when callbacks change
    const onMapReadyRef = useRef(onMapReady);
    const onErrorRef = useRef(onError);
    const onLoadingChangeRef = useRef(onLoadingChange);
    const showPolylineRef = useRef(showPolyline);
    const polylineOptionsRef = useRef(polylineOptions);
    onMapReadyRef.current = onMapReady;
    onErrorRef.current = onError;
    onLoadingChangeRef.current = onLoadingChange;
    showPolylineRef.current = showPolyline;
    polylineOptionsRef.current = polylineOptions;

    // Function to fit bounds to show all markers
    const fitBoundsToAllMarkers = useCallback(() => {
      if (!mapInstanceRef.current || markersRef.current.length <= 1) return;

      const bounds = new google.maps.LatLngBounds();
      for (const marker of markersRef.current) {
        if (marker.position) {
          bounds.extend(marker.position);
        }
      }
      mapInstanceRef.current.fitBounds(bounds);
    }, []);

    // Expose fitBounds function via ref
    useImperativeHandle(ref, () => ({ fitBounds: fitBoundsToAllMarkers }));

    // Paper is a different size from the screen the map was framed for, so re-frame the markers when
    // the page switches into (and back out of) print layout. Without this the printed map shows
    // whatever area sat in the middle of the on-screen view — often no markers, or open ocean for a
    // long-haul flight route.
    useEffect(() => {
      if (typeof window.matchMedia !== 'function') return;
      const printQuery = window.matchMedia('print');
      const refit = () => {
        // Next frame, once the layout has settled at the new size.
        requestAnimationFrame(() => {
          const map = mapInstanceRef.current;
          const [only] = markersRef.current;
          if (!map) return;
          if (markersRef.current.length === 1 && only?.position) map.setCenter(only.position);
          else fitBoundsToAllMarkers();
        });
      };
      printQuery.addEventListener('change', refit);
      window.addEventListener('beforeprint', refit);
      window.addEventListener('afterprint', refit);
      return () => {
        printQuery.removeEventListener('change', refit);
        window.removeEventListener('beforeprint', refit);
        window.removeEventListener('afterprint', refit);
      };
    }, [fitBoundsToAllMarkers]);

    // Initialize Google Maps - only re-run when mapConfig changes
    useEffect(() => {
      const initializeMap = async () => {
        if (!mapRef.current || !Env.VITE_GOOGLE_MAPS_API_KEY) {
          const errorMsg = 'Google Maps API key not found';
          onErrorRef.current?.(errorMsg);
          onLoadingChangeRef.current?.(false);
          return;
        }

        if (!Env.VITE_GOOGLE_MAPS_MAP_ID) {
          const errorMsg = 'Google Maps Map ID is required for AdvancedMarkerElement';
          onErrorRef.current?.(errorMsg);
          onLoadingChangeRef.current?.(false);
          return;
        }

        try {
          onLoadingChangeRef.current?.(true);

          setOptions({ key: Env.VITE_GOOGLE_MAPS_API_KEY, v: 'weekly' });

          await Promise.all([
            importLibrary('maps'),
            importLibrary('places'),
            importLibrary('geocoding'),
            importLibrary('marker'),
          ]);

          // Convert theme preference to Google Maps ColorScheme
          const ColorScheme = google.maps.ColorScheme;
          let colorScheme: google.maps.ColorScheme;
          if (themePreference === 'dark') {
            colorScheme = ColorScheme.DARK;
          } else if (themePreference === 'light') {
            colorScheme = ColorScheme.LIGHT;
          } else {
            // 'system' - follow system preference
            colorScheme = ColorScheme.FOLLOW_SYSTEM;
          }

          // Create map instance with colorScheme
          const map = new google.maps.Map(mapRef.current, { ...mapConfig, colorScheme });

          mapInstanceRef.current = map;

          // Create info window
          infoWindowRef.current = new google.maps.InfoWindow();

          // Add markers for each point
          const markers: google.maps.marker.AdvancedMarkerElement[] = [];
          const path: google.maps.LatLng[] = [];

          for (const point of validPointsRef.current) {
            let position: google.maps.LatLng | null = null;

            if (point.latitude && point.longitude) {
              position = new google.maps.LatLng(point.latitude, point.longitude);
            } else if (point.address) {
              // Geocode the address
              const geocoder = new google.maps.Geocoder();
              try {
                const results = await geocoder.geocode({ address: point.address });
                if (results.results[0]) {
                  position = results.results[0].geometry.location;
                }
              } catch (geocodeError) {
                console.warn(`[GoogleMap] Failed to geocode address: ${point.address}`, geocodeError);
                continue;
              }
            }

            if (position) {
              // Create custom marker element with label. The default anchor is
              // bottom-center of the content. Use negative margin on the circle so
              // the circle extends 10px below the content box, putting the anchor
              // at the circle's center so the polyline meets the center.
              const markerContainer = document.createElement('div');
              markerContainer.style.display = 'flex';
              markerContainer.style.flexDirection = 'column';
              markerContainer.style.alignItems = 'center';
              markerContainer.style.cursor = 'pointer';
              markerContainer.style.position = 'relative';

              // Create label container
              const labelContainer = document.createElement('div');
              labelContainer.style.background = '#ffffff';
              labelContainer.style.color = '#1f2937';
              labelContainer.style.fontSize = '13px';
              labelContainer.style.fontWeight = '500';
              labelContainer.style.padding = '4px 8px';
              labelContainer.style.borderRadius = '4px';
              labelContainer.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
              labelContainer.style.marginBottom = '4px';
              labelContainer.style.maxWidth = '200px';
              labelContainer.style.textAlign = 'center';
              labelContainer.style.position = 'relative';
              labelContainer.style.zIndex = '10';

              // Create label text element
              const labelTextElement = document.createElement('div');
              labelTextElement.textContent = point.label || `Location ${validPointsRef.current.indexOf(point) + 1}`;
              labelTextElement.style.fontWeight = '500';
              labelTextElement.style.whiteSpace = 'nowrap';
              labelTextElement.style.overflow = 'hidden';
              labelTextElement.style.textOverflow = 'ellipsis';

              labelContainer.append(labelTextElement);

              // Create description text element if description exists
              if (point.description) {
                const descriptionElement = document.createElement('div');
                descriptionElement.textContent = point.description;
                descriptionElement.style.fontSize = '11px';
                descriptionElement.style.fontWeight = '400';
                descriptionElement.style.color = '#6b7280';
                descriptionElement.style.marginTop = '2px';
                descriptionElement.style.whiteSpace = 'nowrap';
                descriptionElement.style.overflow = 'hidden';
                descriptionElement.style.textOverflow = 'ellipsis';
                labelContainer.append(descriptionElement);
              }

              // Create marker circle element
              const markerElement = document.createElement('div');
              markerElement.style.width = '20px';
              markerElement.style.height = '20px';
              markerElement.style.borderRadius = '50%';
              markerElement.style.backgroundColor = 'hsl(39, 100%, 41%)'; // Single yellow color for all markers
              markerElement.style.border = '2px solid white';
              markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              markerElement.style.position = 'relative';
              markerElement.style.zIndex = '1';
              markerElement.style.marginBottom = '-10px'; // extend below content so anchor (bottom-center) is at circle center
              // Add pulsing shadow animation
              markerElement.style.animation = 'pulse-shadow 2s ease-in-out infinite';
              // Create and inject keyframes if not already present
              if (!document.querySelector('#google-map-pulse-animation')) {
                const style = document.createElement('style');
                style.id = 'google-map-pulse-animation';
                style.textContent = `
                  @keyframes pulse-shadow {
                    0% {
                      box-shadow: 0 2px 4px rgba(0,0,0,0.3), 0 0 0 0 rgba(208, 135, 0, 0.7);
                    }
                    100% {
                      box-shadow: 0 2px 4px rgba(0,0,0,0.3), 0 0 0 8px rgba(208, 135, 0, 0);
                    }
                  }
                `;
                document.head.append(style);
              }

              // Append elements to container
              if (showLabels) {
                markerContainer.append(labelContainer);
              }
              markerContainer.append(markerElement);

              const marker = new google.maps.marker.AdvancedMarkerElement({
                position,
                map,
                title: point.label || `Location ${validPointsRef.current.indexOf(point) + 1}`,
                content: markerContainer,
              });

              const onClickHandler = point.onClick;
              if (onClickHandler) {
                marker.addListener('click', () => {
                  onClickHandler(map, position);
                });
              }

              markers.push(marker);
            }
            if (position) {
              path.push(position);
            }
          }

          markersRef.current = markers;

          // Draw polyline between points when requested (e.g. for flights)
          if (polylineRef.current) {
            polylineRef.current.setMap(null);
            polylineRef.current = null;
          }
          if (showPolylineRef.current && path.length >= 2) {
            const defaultOptions = { geodesic: true, strokeColor: '#d08700', strokeWeight: 2, strokeOpacity: 0.8 };
            const options = { ...defaultOptions, ...polylineOptionsRef.current };
            polylineRef.current = new google.maps.Polyline({
              path,
              map,
              geodesic: options.geodesic,
              strokeColor: options.strokeColor,
              strokeWeight: options.strokeWeight ?? defaultOptions.strokeWeight,
              strokeOpacity: options.strokeOpacity ?? defaultOptions.strokeOpacity,
            });
          }

          // Handle centering and zooming based on number of markers
          if (markers.length === 1 && markers[0]?.position) {
            // Single marker: center on it with a reasonable zoom level
            map.setCenter(markers[0].position);
            map.setZoom(mapConfig.zoom);
          } else if (markers.length > 1) {
            // Multiple markers: fit bounds to show all
            const bounds = new google.maps.LatLngBounds();
            for (const marker of markers) {
              if (marker.position) {
                bounds.extend(marker.position);
              }
            }
            map.fitBounds(bounds);
          }

          onMapReadyRef.current?.(map);
          onLoadingChangeRef.current?.(false);
        } catch (err) {
          let errorMsg: string;
          if (err instanceof Error && err.message.includes('RefererNotAllowedMapError')) {
            errorMsg = 'Google Maps is not available for this domain.';
          } else {
            errorMsg = 'Failed to load the map';
            console.error('[GoogleMap] Failed to initialize map:', err);
          }
          onErrorRef.current?.(errorMsg);
          onLoadingChangeRef.current?.(false);
        }
      };

      void initializeMap();
    }, [mapConfig, themePreference, systemThemeKey, showPolyline]);

    useEffect(() => {
      // Only listen to system theme changes when user preference is 'system'
      if (themePreference !== 'system') return;

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Trigger map re-initialization by updating systemThemeKey
        setSystemThemeKey((prev) => prev + 1);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }, [themePreference]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        for (const marker of markersRef.current) {
          marker.map = null;
        }
        markersRef.current = [];
        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
      };
    }, []);

    // Early return for invalid cases
    if (!points || points.length === 0) {
      console.warn('[GoogleMap] No points provided');
      return null;
    }

    if (validPoints.length === 0) {
      console.warn('[GoogleMap] No valid points provided');
      return null;
    }

    return <div ref={mapRef} data-print="map" className={cn(className)} />;
  },
);

GoogleMap.displayName = 'GoogleMap';
